import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from './Icon';
import AddContactModal from './AddContactModal';
import UpdateContactModal from './UpdateContactModal';
import { formatFullDate } from '../lib/dateFormat';
import { fileBadge, formatSize } from '../lib/fileBadge';
import { withImpersonate } from '../lib/impersonate';
import { designPointKind, canOpenInDesignPoint, extensionOf, openInDesignPoint } from '../lib/designPoint';
import { resolveCidToUrls } from '../lib/inlineImages';
import * as attachClip from '../lib/attachClipboard';
import { isNoiseAttachment } from './MessageList';
import { isV2 } from '../lib/uiVersion';
import styles from './ReadingPane.module.css';

function initials(name) {
  if (!name) return '?';
  const parts = String(name).split(/[\s|]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Curated emoji set for the quick-reply emoji picker. Same shape as
// the one in ComposeModal so a future refactor can pull both into a
// shared library. Outlook's emoji shortcut is Win+. (Windows native);
// this button gives the same access on every OS without leaving the
// app, matching how the user asked for "the same shortcut/method as
// what outlook does".
const QR_EMOJI = [
  '👍','👎','❤️','😂','😮','😢','😡','🎉','👏','🙏',
  '🔥','✅','❌','⚠️','💯','💡','📌','📎','📅','🕐',
  '😀','😊','🙂','😉','😎','🤔','😅','😴','🙌','🚀',
];

// Outlook surfaces six built-in reactions. When a user picks one of
// these glyphs from the QR picker AND the message is from someone
// inside our M365 tenant, we PATCH the OwnerReactionType MAPI property
// (via /api/mail/messages/:id/react) so the reaction round-trips back
// to the original sender's mailbox. Anything else (other glyphs, or
// external mail) falls back to the original "insert into reply text"
// behaviour, which is still useful for composing replies.
const EMOJI_TO_REACTION = {
  '👍': 'thumbsUp',
  '❤️': 'heart',
  '😂': 'laugh',
  '😮': 'surprised',
  '😢': 'sad',
  '🎉': 'celebrate',
};

function QuickReplyEmojiButton({ onPick, disabled, canReact, onReact }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [reactedAs, setReactedAs] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const id = setTimeout(() => window.addEventListener('click', close), 0);
    return () => { clearTimeout(id); window.removeEventListener('click', close); };
  }, [open]);

  // Reset the "reacted ✓" state when the user opens a different message
  // (canReact identity changes when the message changes).
  useEffect(() => { setReactedAs(null); }, [canReact, onReact]);

  async function handlePick(em) {
    const reactionType = EMOJI_TO_REACTION[em];
    // Only React via Graph for the six mappable Outlook reactions on
    // internal mail. Everything else stays the original insert-into-text.
    if (canReact && onReact && reactionType) {
      setPending(true);
      try {
        await onReact(reactionType);
        setReactedAs(em);
      } catch (err) {
        // Surface the error inline by falling back to text-insert; the
        // worst outcome is the emoji landing in the reply rather than
        // nothing happening at all.
        onPick(em);
      } finally {
        setPending(false);
        setOpen(false);
      }
      return;
    }
    onPick(em);
    setOpen(false);
  }

  return (
    <span className={styles.qrEmojiWrap}>
      <button
        type="button"
        className={styles.qrEmojiBtn}
        onClick={(e) => { e.stopPropagation(); if (!pending) setOpen(o => !o); }}
        disabled={disabled || pending}
        title={
          reactedAs
            ? `You reacted ${reactedAs} — pick again to change`
            : canReact
              ? 'React or insert emoji'
              : 'Insert emoji'
        }
        aria-label={canReact ? 'React with emoji or insert' : 'Insert emoji'}
      >
        {reactedAs || '😊'}
      </button>
      {open && (
        <div
          className={styles.qrEmojiPanel}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Emoji picker"
        >
          {QR_EMOJI.map(em => (
            <button
              key={em}
              type="button"
              onClick={() => handlePick(em)}
              title={
                canReact && EMOJI_TO_REACTION[em]
                  ? `React with ${em}`
                  : `Insert ${em}`
              }
            >{em}</button>
          ))}
        </div>
      )}
    </span>
  );
}

function isImageAttachment(att) {
  const ct = String(att?.contentType || '').toLowerCase();
  if (ct.startsWith('image/')) return true;
  const name = String(att?.name || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(name);
}

function AttachmentChip({ att, downloadUrl, openUrl, previewUrl, onPreview, designPointUrl }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const badge = fileBadge(att);
  // "Open in DesignPoint" (lib/designPoint.js). 'click': artwork a browser tab
  // cannot show (.eps / .ai / .svg) — the chip itself opens DesignPoint.
  // 'button': a PDF keeps its click and gets a DesignPoint button beside it.
  // null: not offered (another file type, or the feature is switched off).
  const dpKind = designPointUrl ? designPointKind(att) : null;
  // The menu's "DesignPoint" item is on EVERY attachment while the feature is
  // on; it works for any format DesignPoint opens and says why not for others.
  const dpOpenable = !!designPointUrl && canOpenInDesignPoint(att);
  const [dpBusy, setDpBusy] = useState(false);
  const openDesignPoint = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setMenuOpen(false);
    if (dpBusy) return;
    setDpBusy(true);
    try {
      await openInDesignPoint({ url: designPointUrl, name: att.name });
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Could not open in DesignPoint: ' + (err.message || 'unknown error'));
    } finally {
      setDpBusy(false);
    }
  };

  // Close on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    const id = setTimeout(() => window.addEventListener('click', close), 0);
    return () => { clearTimeout(id); window.removeEventListener('click', close); };
  }, [menuOpen]);

  // Copy: populate BOTH the browser clipboard (for paste into Outlook/Teams/
  // file managers) AND our in-app attachment clipboard (so the user can Ctrl+V
  // in another email's compose window — the browser's own clipboard won't
  // survive arbitrary MIME types like .msg or .xlsx).
  const copyFile = async () => {
    setMenuOpen(false);
    try {
      const res  = await fetch(downloadUrl);
      const blob = await res.blob();

      // Stash in the in-app clipboard for cross-email paste-to-attach.
      const contentBytes = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const s = String(r.result || '');
          const comma = s.indexOf(',');
          resolve(comma >= 0 ? s.slice(comma + 1) : s);
        };
        r.onerror = () => reject(r.error);
        r.readAsDataURL(blob);
      });
      attachClip.set({
        name:         att.name,
        contentType:  att.contentType || blob.type || 'application/octet-stream',
        contentBytes,
        size:         blob.size,
      });

      // Best-effort system clipboard for paste outside the dashboard. Chrome
      // only accepts a few MIME types (text/plain, text/html, image/png),
      // so most files will silently fail here — that's fine, the in-app
      // copy above already succeeded.
      if (window.ClipboardItem && navigator.clipboard?.write) {
        try {
          const item = new window.ClipboardItem({ [blob.type || 'application/octet-stream']: blob });
          await navigator.clipboard.write([item]);
        } catch { /* system clipboard rejected this MIME type — no-op */ }
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Copy failed: ' + (err.message || 'unknown error'));
    }
  };
  // Fetch-and-blob, so Save As / Open work even when Chrome's Safe
  // Browsing silently kills SVG / .ai downloads done via a same-origin
  // <a href download> link (failure shows up as "Site wasn't available
  // — Resume" with no Network entry — that's Safe Browsing, not us).
  // Going via blob: gives the browser bytes it has already accepted.
  const fetchAsBlob = async (url) => {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  };
  const openInline = async () => {
    setMenuOpen(false);
    try {
      const blob = await fetchAsBlob(openUrl);
      const url  = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Open failed: ' + (err.message || 'unknown error'));
    }
  };
  const saveAs = async () => {
    setMenuOpen(false);
    try {
      const blob = await fetchAsBlob(downloadUrl);
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // If att.name lacks an extension, derive one from blob.type so
      // Chrome doesn't fall back to .txt on octet-stream replies.
      let name = att.name || 'attachment';
      if (!/\.[a-z0-9]{1,5}$/i.test(name)) {
        const mimeExt = {
          'application/pdf':                                                            'pdf',
          'image/png':                                                                  'png',
          'image/jpeg':                                                                 'jpg',
          'image/gif':                                                                  'gif',
          'image/webp':                                                                 'webp',
          'image/svg+xml':                                                              'svg',
          'application/msword':                                                         'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':    'docx',
          'application/vnd.ms-excel':                                                   'xls',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':          'xlsx',
          'application/zip':                                                            'zip',
        }[blob.type] || '';
        if (mimeExt) name += '.' + mimeExt;
      }
      a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Download failed: ' + (err.message || 'unknown error'));
    }
  };

  // Drag-out support: when the user drags the chip onto their OS
  // desktop / file manager, the browser fetches `downloadUrl` and saves
  // it as `att.name`. Same DownloadURL meta-format MessageList uses for
  // dragging messages out as .eml.
  const onDragStart = (e) => {
    // Same extension-derivation as saveAs(): if att.name has no
    // extension we'd save as e.g. "attachment" with no extension and
    // Windows can decide to append .txt based on the mime type, which
    // is the reported symptom.
    let dragName = String(att.name || 'attachment').replace(/[\\/:*?"<>|]+/g, '_');
    const dragMime = att.contentType || 'application/octet-stream';
    if (!/\.[a-z0-9]{1,5}$/i.test(dragName)) {
      const ext = {
        'application/pdf':                                                            'pdf',
        'image/png':                                                                  'png',
        'image/jpeg':                                                                 'jpg',
        'image/gif':                                                                  'gif',
        'image/webp':                                                                 'webp',
        'image/svg+xml':                                                              'svg',
        'application/msword':                                                         'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':    'docx',
        'application/vnd.ms-excel':                                                   'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':          'xlsx',
        'application/zip':                                                            'zip',
      }[dragMime] || '';
      if (ext) dragName += '.' + ext;
    }
    const fullUrl = window.location.origin + downloadUrl;
    try {
      e.dataTransfer.setData(
        'DownloadURL',
        `${dragMime}:${dragName}:${fullUrl}`
      );
    } catch { /* internal drop still works via text/uri-list below */ }
    e.dataTransfer.setData('text/uri-list', fullUrl);
    e.dataTransfer.setData('text/plain', dragName);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <span className={styles.attachment} draggable onDragStart={onDragStart}>
      <a
        className={styles.attachmentMain}
        href={openUrl}
        target="_blank"
        rel="noopener noreferrer"
        draggable={false}
        onClick={dpKind === 'click' ? openDesignPoint : undefined}
        style={dpBusy ? { opacity: 0.6, cursor: 'progress' } : undefined}
        title={`${att.name}${att.size ? ` — ${formatSize(att.size)}` : ''}\n${dpKind === 'click' ? 'Click to open in DesignPoint · ' : ''}Drag onto desktop to download`}
      >
        {isImageAttachment(att) ? (
          <img
            src={openUrl}
            alt=""
            loading="lazy"
            className={styles.attachmentThumb}
            style={{
              width: 28, height: 28, objectFit: 'cover',
              borderRadius: 4, marginRight: 6, flexShrink: 0,
              background: '#f4f5f7',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span
            className={styles.attachmentBadge}
            style={{ background: badge.bg, color: badge.fg }}
          >
            {badge.label}
          </span>
        )}
        <span className={styles.attachmentName}>{att.name || 'attachment'}</span>
        {att.size ? <span className={styles.attachmentSize}>{formatSize(att.size)}</span> : null}
      </a>
      {dpKind === 'button' && (
        <button
          className={styles.attachmentMore}
          style={{ width: 'auto', padding: '0 7px', fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3 }}
          onClick={openDesignPoint}
          disabled={dpBusy}
          title="Open in DesignPoint (new tab)"
        >
          {dpBusy ? '…' : 'DP'}
        </button>
      )}
      <button
        className={styles.attachmentMore}
        onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }}
        title="More"
      >
        <Icon name="chevronDown" size={11} />
      </button>
      {menuOpen && (
        <div className={styles.attachmentMenu} onClick={e => e.stopPropagation()}>
          <button onClick={() => { setMenuOpen(false); onPreview && onPreview(att, previewUrl); }}>Preview</button>
          <button onClick={copyFile}>Copy</button>
          <button onClick={openInline}>Open</button>
          {designPointUrl && (
            <button
              onClick={dpOpenable ? openDesignPoint : undefined}
              disabled={!dpOpenable}
              title={dpOpenable
                ? 'Open this file in DesignPoint (new tab)'
                : `DesignPoint can't open .${extensionOf(att.name) || 'this'} files`}
            >
              DesignPoint
            </button>
          )}
          <button onClick={saveAs}>Download</button>
        </div>
      )}
    </span>
  );
}

// In-pane attachment preview. Sits absolute over the reading content so the
// underlying message + reply bar stay mounted (no scroll loss when closing).
// Picks a renderer per file kind:
//   • images          → <img>
//   • pdf, ai         → <iframe> on the preview URL (server streams .ai as PDF)
//   • eps             → <iframe> on the preview URL (server gs-converts to PDF)
//   • text-ish        → <iframe>
//   • everything else → "Preview not available" with Download fallback
function AttachmentPreviewOverlay({ att, url, onClose }) {
  const ext = String(att?.name || '').toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || '';
  const ct  = String(att?.contentType || '').toLowerCase();
  const isImage = ct.startsWith('image/') || ['png','jpg','jpeg','gif','webp','bmp','svg'].includes(ext);
  const isPdf   = ct === 'application/pdf' || ext === 'pdf';
  const isText  = ct.startsWith('text/') || ['txt','csv','log','json','xml','html','md'].includes(ext);
  const isXlsx  = ['xlsx','xls','xlsm'].includes(ext);
  // .eml previews go through the server's eml-to-html renderer; the
  // `text/html` response shape lets the iframe render it natively.
  // Detect via extension OR the message/rfc822 content type (which is
  // what Graph itemAttachments report).
  const isEml = ext === 'eml' || /message\/rfc822/.test(ct);
  const renderable = isImage || isPdf || isText || isXlsx || isEml || ext === 'ai' || ext === 'eps';

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={styles.previewOverlay} role="dialog" aria-label="Attachment preview">
      <div className={styles.previewBar}>
        <span className={styles.previewName}>{att?.name || 'Attachment'}</span>
        <a className={styles.previewBtn} href={url.replace(/\?preview=1$/, '')} download={att?.name}>Download</a>
        <button className={styles.previewBtn} onClick={onClose} aria-label="Close preview">✕</button>
      </div>
      <div className={styles.previewBody}>
        {isImage && <img src={url} alt={att?.name || ''} />}
        {!isImage && renderable && (
          <iframe title={att?.name || 'preview'} src={url} />
        )}
        {!renderable && (
          <div className={styles.previewUnavailable}>
            <p>No inline preview for <code>.{ext || 'file'}</code> yet.</p>
            <p>Use Download or Open from the menu to view it.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// View Account button — probes /api/onpoint/customer-match for the
// sender's address before rendering itself. Hidden when the address
// isn't a known OnPoint customer (so external prospects don't get
// a button that would fail with "no customer found"). The probe is
// cheap (single MySQL row) and cached per-render via the from prop.
// View Account button — always visible on external emails. The
// drawer itself gracefully handles "no OnPoint customer found"
// (and the multi-account picker handles >1 match), so we don't
// need to pre-gate on a customer-match probe; that probe also
// missed cases where the sender isn't in customers.email but
// the company is recorded under a different contact (e.g.
// emails on user-added labels). Removed the gate per user
// request 2026-05-06.
function ViewAccountButton({ from, onOpenAccount, className }) {
  if (!from) return null;
  return (
    <button
      className={className || `${styles.tbBtn} ${styles.desktopOnly}`}
      title="View customer account in Xero (outstanding, overdue, invoices)"
      onClick={() => onOpenAccount(from)}
    >
      <Icon name="folder" size={13} stroke={2.2} /> View Account
    </button>
  );
}
// Build Order button — sits in the top-bar like View Account / Upload
// Invoice. App.js gates rendering on the message's label === 'Orders'
// (mirroring UploadInvoiceButton's 'Accounts' gate). This button itself
// is dumb — it only fires the parent's onClick. The actual drawer
// (BuildOrderDrawer) is mounted up in App.js the same way the order /
// account drawers are, so the drawer survives navigation between
// emails while it's open. See bot/docs/raise-order-draft-endpoint-spec.md
// for the full feature spec.
function BuildOrderButton({ onClick, className }) {
  return (
    <button
      className={className || `${styles.tbBtn} ${styles.desktopOnly}`}
      title="Build an order from this email and related ones — selects files, renames, sends to OnPoint"
      onClick={onClick}
    >
      <Icon name="archive" size={13} stroke={2.2} /> Build Order
    </button>
  );
}

export { ViewAccountButton, UploadInvoiceButton, BuildOrderButton };

// Upload-Invoice button. Pops a small chooser when the email has
// multiple PDFs; single-PDF case uploads directly. Ships the chosen
// attachment to /api/xero/upload-bill which creates a DRAFT bill in
// Xero and attaches the PDF. On success surfaces a "View in Xero"
// link inline so the accountant can finish the bill there.
function UploadInvoiceButton({ message, pdfs, className, onRequestUpload }) {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const hasAttachments = pdfs.length > 0;

  function fromEmail(att) {
    setOpen(false);
    onRequestUpload?.({ messageId: message.id, attachmentId: att.id, name: att.name });
  }

  async function fromFile(file) {
    setOpen(false);
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(String(reader.result).replace(/^data:[^;]+;base64,/, ''));
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
    onRequestUpload?.({
      fileName:    file.name,
      contentType: file.type || 'application/pdf',
      base64,
    });
  }

  function pickFile() {
    setOpen(false);
    fileInputRef.current?.click();
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) fromFile(f); e.target.value = ''; }}
      />
      <button
        className={className || `${styles.tbBtn} ${styles.desktopOnly}`}
        title="Upload an invoice PDF to Xero — opens a review drawer"
        onClick={() => hasAttachments ? setOpen(o => !o) : pickFile()}
      >
        <Icon name="archive" size={13} stroke={2.2} /> Upload Invoice
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#fff', border: '1px solid #d4d9e5', borderRadius: 8, boxShadow: '0 6px 18px rgba(15,26,74,0.12)', padding: 6, zIndex: 20, minWidth: 260 }}>
          <div style={{ fontSize: 10.5, color: '#5b6678', padding: '4px 8px', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>From this email</div>
          {pdfs.map(att => (
            <button key={att.id}
                    onClick={() => fromEmail(att)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 0, padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4 }}
                    onMouseDown={e => e.currentTarget.style.background = '#f1f4fa'}
                    onMouseUp={e => e.currentTarget.style.background = 'transparent'}>
              📎 {att.name}
            </button>
          ))}
          <div style={{ height: 1, background: '#eef0f7', margin: '6px 4px' }} />
          <div style={{ fontSize: 10.5, color: '#5b6678', padding: '4px 8px', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>Or from your computer</div>
          <button
            onClick={pickFile}
            style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 0, padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4 }}
            onMouseDown={e => e.currentTarget.style.background = '#f1f4fa'}
            onMouseUp={e => e.currentTarget.style.background = 'transparent'}>
            📁 Browse for PDF…
          </button>
        </div>
      )}
    </span>
  );
}

export default function ReadingPane({
  message,
  // senderInfo is owned by App.js so the topbar's New Contact /
  // New Customer pill and the reading pane share one source of
  // truth. addingContact + handlers similarly live one level up so
  // the topbar pill can open the modal without ReadingPane needing
  // to expose imperative handles.
  senderInfo = null,
  // "Open in DesignPoint" on artwork attachments — the server's switch
  // (DESIGNPOINT_OPEN_ENABLED), passed down from /api/auth/me.
  designPointEnabled = false,
  showExternalContent = false,
  addingContact = false,
  onCloseAddContact,
  onContactAdded,
  updatingContact = false,
  onCloseUpdateContact,
  onContactUpdated,
  onSwitchToAddContact,
  onBack,          // mobile: returns to message list; hidden on desktop via CSS
  onReply,
  onReplyAll,
  onForward,
  onDelete,
  onFlag,          // legacy boolean toggle (unused now; kept for back-compat)
  onFlagCycle,     // 3-state cycle none → flagged → complete → none
  onMarkUnread,
  onAiReply,
  onOpenAccount,
  onManageReplyOpeners,
  // onQuickSend prop removed — consumer was the deleted quick-reply
  // bar. Parent (App.js) still passes it for back-compat with older
  // bundles; ignored here. Safe to drop from App.js too when convenient.
  composeOverlay,
  // `tabs` is a unified list of in-progress drafts AND samples PLUS an
  // 'email' marker entry that appears whenever a modal is open over
  // this email. Each entry has { kind: 'draft' | 'sample' | 'email',
  // key, … }. ReadingPane just renders them; the parent decides what
  // each click does via the kind-specific handlers.
  tabs = [],
  // The key of the tab that's currently mounted in the modal slot.
  // Used to render a blue "you're viewing this" highlight on the
  // matching tab so it's obvious which draft / sample is open.
  activeTabKey = null,
  onOpenDraft,
  onDiscardDraft,
  onOpenSample,
  onDiscardSample,
  // Build Order tabs — multi-tab support (2026-06-05). Each in-progress
  // Build Order appears as a tab in the draft strip; clicking the tab
  // activates that order's drawer, × discards it entirely.
  onOpenBuildOrder,
  onDiscardBuildOrder,
  // AI Quote tab — restore the minimised quote panel / close the quote.
  onOpenQuote,
  onDiscardQuote,
  onOpenEmail,
  // JSX rendered on the right side of the tab strip. Used by App.js to
  // place Submit + Cancel buttons next to the tabs when AddSample is
  // open — keeps the form chrome (modal) decoupled from the navigation
  // chrome (tabs + actions), and gives the user a single bottom strip
  // for "in-progress work + actions on it".
  tabBarRight = null,
  // Configured tenant domain (e.g. "pinpoint.promo"). When the message's
  // sender is on this domain, the QuickReplyEmojiButton uses six common
  // glyphs to set Outlook reactions via /api/mail/messages/:id/react
  // instead of inserting them into the reply text.
  tenantDomain = '',
  // Async (id, reactionType) → response. Invoked by the quick-reply
  // emoji picker on internal mail. Throws on failure; the picker
  // falls back to text-insert in that case.
  onReactToMessage,
}) {
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiError,       setAiError]       = useState(null);
  const [moreOpen,      setMoreOpen]      = useState(false);
  const [downloading,   setDownloading]   = useState(false);
  const [preview,       setPreview]       = useState(null);   // { att, url }
  const iframeRef = useRef(null);

  useEffect(() => {
    // quickText / quickSending state removed alongside the deleted
    // quick-reply bar — only moreOpen still needs resetting per
    // message change.
    setMoreOpen(false);
  }, [message?.id]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = () => setMoreOpen(false);
    const id = setTimeout(() => window.addEventListener('click', close), 0);
    return () => { clearTimeout(id); window.removeEventListener('click', close); };
  }, [moreOpen]);

  // NB: every hook below must run on every render, regardless of whether
  // `message` is null, or React trips "rendered fewer hooks than expected"
  // and unmounts the whole tree. Guards inside the hooks, not above them.
  //
  // No bodyText fallback DURING loading — the cache primes bodyText
  // (stripHtml output, already whitespace-collapsed) but not bodyHtml,
  // and rendering it produced the "wall of text" flash users saw when
  // clicking an email. Once the full-message fetch has landed
  // (fullBodyLoaded), if bodyHtml is still empty we fall back to
  // bodyText so plain-text-only emails (some senders / mailer outputs
  // ship no HTML part) aren't displayed as blank — same fallback the
  // reply quote builder uses.
  let rawBodyHtml = message?.bodyHtml || '';
  const fullBodyLoaded = Array.isArray(message?.attachments);
  if (!rawBodyHtml && fullBodyLoaded && message?.bodyText) {
    rawBodyHtml = String(message.bodyText)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  }

  const bodyHtml = useMemo(() => {
    if (!rawBodyHtml) return rawBodyHtml;
    // Scrub the HTML so the sandboxed iframe has nothing left for the
    // browser to block and warn about. DOMParser is inert — it doesn't
    // execute scripts or fetch resources — so it's the safe way to use
    // the browser's own HTML parser for this.
    const doc = new DOMParser().parseFromString(rawBodyHtml, 'text/html');
    doc.querySelectorAll('script, noscript, iframe, object, embed, frame, frameset, meta[http-equiv]').forEach(el => el.remove());
    doc.querySelectorAll('*').forEach(el => {
      for (const attr of Array.from(el.attributes)) {
        const n = attr.name.toLowerCase();
        if (n.startsWith('on')) {
          el.removeAttribute(attr.name);
        } else if ((n === 'href' || n === 'src' || n === 'xlink:href' || n === 'action' || n === 'formaction') &&
                   /^\s*javascript:/i.test(attr.value)) {
          el.removeAttribute(attr.name);
        }
      }
    });
    // Strip Microsoft Defender / Outlook "blocked by your organisation"
    // and external-sender banners that get injected by ATP / mailflow
    // rules. They render as a giant red/yellow strip at the top of every
    // forwarded email and are pure noise inside the dashboard. The
    // bottom-left "Show external content" toggle (showExternalContent)
    // restores them when the user genuinely needs to see what the org
    // policy tagged. Heuristic: any element whose text content matches
    // a few well-known phrases. Scoped to the *first 800 chars* so a
    // legitimate quote of those phrases later in the body isn't dropped.
    if (!showExternalContent) {
      const banners = Array.from(doc.querySelectorAll('div, table, span, p'))
        .slice(0, 60); // top of the document only
      const phraseRe = /\b(blocked by your organi[sz]ation|external sender|caution: this email originated|some content in this message|your administrator has blocked|message has been quarantined)\b/i;
      for (const el of banners) {
        const txt = (el.textContent || '').slice(0, 800);
        if (!phraseRe.test(txt)) continue;
        const isBlock = /^(div|table|p|tr)$/i.test(el.tagName);
        if (!isBlock || el.children.length >= 8) continue;
        // Only strip when the matched element really IS a banner — a
        // short standalone strip of text. Outlook wraps forwarded
        // chains in a single outer div that contains, among other
        // things, the original sender's "CAUTION: external email"
        // notice; the < 8 children gate alone doesn't catch that
        // wrapper (it's often 3-4 children: header table, banner,
        // body table). Require the banner phrase to make up most of
        // the element's text AND the total text to be short. A real
        // banner is rarely longer than ~400 chars; a wrapper around
        // a forwarded chain is thousands.
        const fullText = (el.textContent || '').trim();
        if (fullText.length > 400) continue;
        el.remove();
      }
    }
    // Upgrade http: → https: on images so Chrome doesn't log mixed-content
    // warnings. Chrome auto-upgrades these anyway; doing it ourselves just
    // silences the console noise.
    doc.querySelectorAll('img, image').forEach(el => {
      for (const a of ['src', 'xlink:href']) {
        const v = el.getAttribute(a);
        if (v && /^http:\/\//i.test(v)) el.setAttribute(a, v.replace(/^http:\/\//i, 'https://'));
      }
    });
    // Convert embedded \n in visible text nodes to <br>. HTML treats
    // raw newlines as ordinary whitespace, so an email whose body is
    // "Hi Darren\n\nPlease..." renders as one wall of text. This bites
    // both true plain-text emails (whole body is one text node) and
    // forwarded HTML where the sender's client serialised paragraphs
    // with bare \n instead of <br>/<p>. Skipping pure-whitespace text
    // nodes leaves real inter-tag whitespace untouched, so properly
    // structured emails (paragraphs, signature tables) are unaffected.
    const SKIP_PARENTS = new Set(['PRE', 'STYLE', 'SCRIPT', 'TEXTAREA', 'CODE']);
    const walker = doc.createTreeWalker(doc.body || doc, NodeFilter.SHOW_TEXT, null);
    const targets = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.nodeValue || node.nodeValue.indexOf('\n') === -1) continue;
      if (!node.nodeValue.trim()) continue; // inter-tag whitespace
      let p = node.parentNode;
      let skip = false;
      while (p && p.nodeType === 1) {
        if (SKIP_PARENTS.has(p.tagName)) { skip = true; break; }
        p = p.parentNode;
      }
      if (!skip) targets.push(node);
    }
    for (const node of targets) {
      const parts = node.nodeValue.split('\n');
      const frag = doc.createDocumentFragment();
      parts.forEach((part, i) => {
        if (part) frag.appendChild(doc.createTextNode(part));
        if (i < parts.length - 1) frag.appendChild(doc.createElement('br'));
      });
      node.parentNode.replaceChild(frag, node);
    }
    // Strip fixed heights / overflow on the email's html and body
    // elements, plus the topmost wrapper divs. Some senders (e.g.
    // Outlook desktop on a forwarded chain) emit `height: 100%` /
    // `min-height: 100vh` / `overflow: auto` which makes the iframe
    // appear to scroll inside itself instead of growing to fit the
    // measured scrollHeight. Only touch the outermost layout
    // wrappers so we don't flatten internal table layouts the email
    // relied on.
    function stripVerticalSizing(el) {
      if (!el || el.nodeType !== 1) return;
      const s = el.getAttribute('style');
      if (s) {
        const cleaned = s
          .replace(/(^|;)\s*(height|min-height|max-height)\s*:[^;]*/gi, '$1')
          .replace(/(^|;)\s*overflow(?:-y)?\s*:[^;]*/gi, '$1')
          .replace(/^;+/, '').trim();
        if (cleaned) el.setAttribute('style', cleaned);
        else el.removeAttribute('style');
      }
      ['height'].forEach(a => el.hasAttribute(a) && el.removeAttribute(a));
    }
    stripVerticalSizing(doc.documentElement);
    stripVerticalSizing(doc.body);
    // Recursively strip height/overflow from EVERY div / table / td /
    // section in the body. The previous "first 2 wrapper divs" pass
    // didn't catch deeply-nested fixed-height wrappers some forwarded
    // chains insert (e.g. an Outlook .ReadMsgBody class with inline
    // height: 100vh on a 4-deep wrapper). Limited to layout
    // containers so images/inline objects keep their explicit
    // dimensions.
    const LAYOUT_TAGS = new Set(['DIV', 'TABLE', 'TBODY', 'THEAD', 'TR', 'TD', 'TH', 'SECTION', 'ARTICLE', 'MAIN', 'ASIDE', 'HEADER', 'FOOTER', 'NAV']);
    if (doc.body) {
      const all = doc.body.getElementsByTagName('*');
      for (let i = 0; i < all.length; i++) {
        if (LAYOUT_TAGS.has(all[i].tagName)) stripVerticalSizing(all[i]);
      }
    }
    // Use the email's body INNER HTML, not the full document outer HTML.
    // Outlook and many marketing senders ship `<body style="max-width:
    // 600px; margin: 0 auto">…` — when we previously serialised
    // documentElement.outerHTML and wrapped it in our iframe's <body>,
    // the browser hoisted the email's body attributes onto our body and
    // the rendered content was clamped to a narrow centred column with
    // whitespace either side, regardless of the iframe's actual width.
    // Dropping back to body.innerHTML gives our iframe srcDoc full
    // control of the body element's sizing.
    let html = doc.body ? doc.body.innerHTML : (doc.documentElement ? doc.documentElement.outerHTML : '');
    const inline = message?.inlineAttachments || [];
    if (!inline.length) return html;
    // Prefer URL refs over data: URIs. The bot ships inline image
    // metadata only (attachmentId + contentId) and serves bytes on
    // demand via /api/mail/messages/:id/inline/:aid with immutable
    // caching. This cut a multi-MB message-open JSON down to ~50 KB
    // and let the browser cache images across re-opens. Legacy
    // contentBytes path still honoured for any caller that pulled
    // the message with ?inline=1.
    //
    // resolveCidToUrls bracket-normalises the cid the SAME way every other
    // helper does — this map was the ONE left un-normalised, so a bracketed
    // sender rendered broken inline images in the READER while replies were
    // fine. withImpersonate: the iframe's <img src> loads carry no fetch
    // headers, so when viewing another mailbox the target must ride as ?as=
    // or the server resolves the admin's own mailbox and the image 404s
    // (blank IMG_xxxx.jpg boxes, 2026-07-15).
    const urlFor = message?.id
      ? aid => withImpersonate(`/api/mail/messages/${encodeURIComponent(message.id)}/inline/${encodeURIComponent(aid)}`)
      : null;
    return resolveCidToUrls(html, inline, urlFor);
  }, [rawBodyHtml, message?.inlineAttachments, message?.id, showExternalContent]);

  // Iframe autosize. Runs on every bodyHtml change. Replaces a
  // JSX-level onLoad handler that was unreliable — React updates
  // srcDoc on the existing iframe DOM element when bodyHtml
  // changes, and the browser doesn't always re-fire `load` in
  // that case, so onLoad-bound autosize silently skipped on every
  // email after the first (sticky 200 px min-height + internal
  // scrollbar). useEffect runs deterministically after the render
  // that changed bodyHtml, polls until the iframe's body is
  // parsed, then attaches a ResizeObserver for ongoing layout
  // changes (image arrival, font swap, error placeholders).
  // Iframe sizing — REBUILT.
  //
  // We used to measure the email body's scrollHeight via ResizeObserver
  // + MutationObserver + multi-stage timeouts and resize the iframe to
  // match. That approach kept clipping random emails (signatures cut
  // mid-line, quoted history dropped) because every measurement path
  // can return a value smaller than the truth — and a single bad
  // measurement freezes the iframe at the wrong size.
  //
  // New approach: iframe takes a fixed CSS height (the reading-pane
  // viewport) and scrolls its OWN body when content is taller. No
  // measurement loop. Long emails get an inner scrollbar; short emails
  // show as-is. forceUnclamp still runs once per body change so the
  // sender's max-height/overflow:hidden styles can't lock content
  // inside the iframe.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !bodyHtml) return undefined;
    let cancelled = false;
    function forceUnclampOnce() {
      try {
        const win = iframe.contentWindow;
        const doc = win?.document;
        if (!doc?.body) return;
        const SKIP = new Set(['IMG', 'VIDEO', 'IFRAME', 'EMBED', 'OBJECT', 'CANVAS', 'PICTURE']);
        const all = [doc.documentElement, doc.body, ...doc.body.getElementsByTagName('*')];
        for (const el of all) {
          if (!el?.style || SKIP.has(el.tagName)) continue;
          el.style.setProperty('max-height', 'none',    'important');
          el.style.setProperty('overflow',   'visible', 'important');
          el.style.setProperty('overflow-y', 'visible', 'important');
          if (el.hasAttribute && el.hasAttribute('height')) el.removeAttribute('height');
        }
      } catch {}
    }
    const timers = [];
    function attach() {
      if (cancelled) return;
      const win  = iframe.contentWindow;
      const body = win?.document?.body;
      if (!body) { timers.push(setTimeout(attach, 30)); return; }
      forceUnclampOnce();
      // Late re-runs catch slow font swaps / lazy-loaded image classes
      // injecting fresh max-heights.
      [200, 800, 2500].forEach(ms => timers.push(setTimeout(forceUnclampOnce, ms)));
    }
    attach();
    // Clear the deferred unclamp timers on cleanup so they can't fire after
    // unmount / body change (Section 6 smaller improvement).
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [bodyHtml]);


  // Shared render for the draft-tabs strip — used in BOTH the
  // empty-state branch (no message selected) and the full reader
  // branch below, so any open drafts / sample tabs / tabBarRight
  // buttons stay visible even when the user navigates away from
  // the current email (clicks a folder, clears a thread, etc.).
  //
  // ALWAYS renders, even with zero tabs and no tabBarRight — the
  // strip is the persistent compose surface (drafts land here, send
  // status surfaces here) and the user asked for it to be visible at
  // all times. When empty, it renders as a thin strip with a subtle
  // "No drafts" hint so it doesn't read as a broken UI element. The
  // moment a compose / sample / AddSample tab opens, the hint is
  // replaced by the tab buttons.
  function renderDraftTabs() {
    const hasContent = (tabs && tabs.length > 0) || tabBarRight;
    if (!hasContent) {
      // /V2 drops the strip while it's empty so the email gets the room;
      // it appears the moment a draft / sample / order / quote opens.
      if (isV2()) return null;
      // Empty strip — visible, but no placeholder text. User asked for
      // a persistent bar without the "No drafts" hint that previously
      // sat here.
      return <div className={styles.draftTabs} aria-label="Drafts" />;
    }
    return (
      <div className={styles.draftTabs}>
        {(tabs || []).map(t => {
          if (t.kind === 'email') {
            // Synthetic tab present only while a modal (compose or
            // AddSample) is open. Click → modal snapshot + close,
            // user lands back on the email read view.
            const label = t.subject ? (t.subject.length > 24 ? t.subject.slice(0, 23) + '…' : t.subject) : 'Email';
            return (
              <button
                key={t.key}
                className={`${styles.draftTab} ${styles.emailTab || ''}`}
                title={`Back to email: ${t.subject || ''}`}
                onClick={() => onOpenEmail && onOpenEmail()}
              >
                <Icon name="mail" size={11} />
                <span className={styles.draftTabText}>Email · {label}</span>
              </button>
            );
          }
          const isActive = activeTabKey && t.key === activeTabKey;
          if (t.kind === 'sample') {
            const label = t.customerName || t.sourceFrom || 'New sample';
            return (
              <button
                key={t.key}
                className={`${styles.draftTab} ${styles.sampleTab || ''} ${isActive ? styles.draftTabActive : ''}`}
                title={`Sample for ${label}`}
                onClick={() => onOpenSample && onOpenSample(t.key)}
              >
                <Icon name="paperclip" size={11} />
                <span className={styles.draftTabText}>Sample · {label}</span>
                <button
                  type="button"
                  className={styles.draftTabClose}
                  title="Discard sample"
                  onClick={(e) => { e.stopPropagation(); onDiscardSample && onDiscardSample(t.key); }}
                >
                  <Icon name="x" size={9} />
                </button>
              </button>
            );
          }
          if (t.kind === 'buildOrder') {
            // Build Order tab — clicks activate that order's drawer
            // (which stays mounted but hidden so all ticks/PO/renames
            // survive switches). × discards the order entirely.
            const raw = t.anchorSubject || t.anchorFromName || 'Order';
            const label = raw.length > 24 ? raw.slice(0, 23) + '…' : raw;
            return (
              <button
                key={t.key}
                className={`${styles.draftTab} ${isActive ? styles.draftTabActive : ''}`}
                title={`Build Order from: ${t.anchorSubject || ''}`}
                onClick={() => onOpenBuildOrder && onOpenBuildOrder(t.key)}
              >
                <Icon name="archive" size={11} />
                <span className={styles.draftTabText}>Order · {label}</span>
                <button
                  type="button"
                  className={styles.draftTabClose}
                  title="Discard order"
                  onClick={(e) => { e.stopPropagation(); onDiscardBuildOrder && onDiscardBuildOrder(t.key); }}
                >
                  <Icon name="x" size={9} />
                </button>
              </button>
            );
          }
          if (t.kind === 'quote') {
            // AI Quote tab — the panel stays mounted (hidden) while
            // minimised so its draft edits survive; click restores it,
            // × closes the quote entirely.
            const raw = t.fromName || t.subject || 'Quote';
            const label = raw.length > 24 ? raw.slice(0, 23) + '…' : raw;
            return (
              <button
                key={t.key}
                className={`${styles.draftTab} ${isActive ? styles.draftTabActive : ''}`}
                title={`AI Quote: ${t.subject || ''}`}
                onClick={() => onOpenQuote && onOpenQuote()}
              >
                <Icon name="sent" size={11} />
                <span className={styles.draftTabText}>Quote · {label}</span>
                <button
                  type="button"
                  className={styles.draftTabClose}
                  title="Close quote"
                  onClick={(e) => { e.stopPropagation(); onDiscardQuote && onDiscardQuote(); }}
                >
                  <Icon name="x" size={9} />
                </button>
              </button>
            );
          }
          return (
            <button
              key={t.key}
              className={`${styles.draftTab} ${isActive ? styles.draftTabActive : ''}`}
              title={t.subject || '(no subject)'}
              onClick={() => onOpenDraft && onOpenDraft(t.key)}
            >
              <Icon name="draft" size={11} />
              <span className={styles.draftTabText}>
                {t.mode === 'reply' ? 'Re: ' : t.mode === 'replyAll' ? 'Re: ' : t.mode === 'forward' ? 'Fwd: ' : ''}
                {t.subject || '(no subject)'}
              </span>
              <button
                type="button"
                className={styles.draftTabClose}
                title="Discard draft"
                onClick={(e) => { e.stopPropagation(); onDiscardDraft && onDiscardDraft(t.key); }}
              >
                <Icon name="x" size={9} />
              </button>
            </button>
          );
        })}
        {tabBarRight && (
          <div className={styles.tabBarRight}>{tabBarRight}</div>
        )}
      </div>
    );
  }

  if (!message) {
    // "New Email" can be opened from the sidebar with no message
    // selected — render compose in the same flex slot pattern so it
    // owns the whole reader. Otherwise show the empty-state mark.
    return (
      <main className={`${styles.reader} ${composeOverlay ? '' : styles.readerEmpty}`}>
        {composeOverlay ? (
          <div className={styles.composeSlot}>{composeOverlay}</div>
        ) : (
          <div className={styles.empty}>
            <div className={styles.emptyMark}><Icon name="mail" size={36} stroke={1.2} /></div>
            <div className={styles.emptyText}>Select a message to read</div>
          </div>
        )}
        {renderDraftTabs()}
      </main>
    );
  }

  async function handleAiReply() {
    setAiLoading(true);
    setAiError(null);
    try {
      await onAiReply(message);
    } catch (err) {
      setAiError(err.message || 'AI reply failed');
    } finally {
      setAiLoading(false);
    }
  }

  // handleQuickSend removed alongside the quick-reply bar that consumed
  // it. Reply / Reply All / Forward / AI Reply in the toolbar are the
  // canonical compose entry points.

  return (
    <main className={styles.reader}>
      {composeOverlay ? (
        /* Compose modal renders in the same flex slot as AddSample so
           the tabs strip + Send button + draft list all stay visible
           below it. The previous overlay-positioning approach hid the
           Send button when the tabs strip surfaced above it; this one
           lets normal flex flow handle the layout. */
        <div className={styles.composeSlot}>{composeOverlay}</div>
      ) : (<>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        {onBack && (
          <button
            className={styles.backBtn}
            onClick={onBack}
            title="Back to list"
            aria-label="Back to message list"
          >
            <Icon name="arrowLeft" size={16} />
          </button>
        )}
        <div className={styles.toolbarGroup}>
          <button className={`${styles.tbBtn} ${styles.tbBtnPrimary}`} onClick={() => onReplyAll(message)}>
            <Icon name="replyAll" size={14} /> Reply All
          </button>
          <button className={styles.tbBtn} onClick={() => onReply(message)}>
            <Icon name="reply" size={14} /> Reply
          </button>
          <button className={styles.tbBtn} onClick={() => onForward(message)}>
            <Icon name="forward" size={14} /> Forward
          </button>
        </div>
        <div className={styles.toolbarRight}>
          {/* View Account + Upload Invoice live in the App-level top
              bar (see App.js header) so the user can hit them
              without scrolling the reader. They're contextual to
              the currently-selected message. */}
          {/* AI Reply — shown on every email. Opens the approval panel
              that uses retrieval + customer context to draft a reply.
              The pipeline already creates an approval_queue row for every
              external email (see bot/src/email/webhook.js#processEmail),
              so the panel works on all labels. Output may look quote-
              shaped on non-quote emails (carryover from the original
              prompt) — that's intentional during the training-on-all-
              emails rollout: staff edits / discards on non-quotes feed
              the prompt-refinement loop. */}
          <button
            className={`${styles.tbBtn} ${styles.tbBtnAi} ${styles.desktopOnly}`}
            title="Generate an AI reply"
            onClick={handleAiReply}
            disabled={aiLoading}
          >
            {aiLoading ? (
              <><span className={styles.aiSpinner} /> Generating…</>
            ) : (
              <><Icon name="sparkle" size={14} /> AI Reply</>
            )}
          </button>
          <span className={styles.toolbarSep} />
          {/* Emoji-react button. Previously lived inside the deleted
              quick-reply bar — moved here so the feature survives the
              bar's removal. Posts an Outlook reaction (👍 / ❤️ / etc)
              via /api/mail/messages/:id/react. Available on every
              message; canReact gates the picker UI per-sender. */}
          {onReactToMessage && (
            <QuickReplyEmojiButton
              onPick={() => { /* no-op without an input — pure reaction picker now */ }}
              disabled={false}
              canReact={true}
              onReact={(reactionType) => onReactToMessage(message.id, reactionType)}
            />
          )}
          <button
            className={`${styles.tbBtn} ${styles.tbBtnIcon} ${message.completed ? styles.tbBtnComplete : message.flagged ? styles.tbBtnFlagged : ''}`}
            title={
              message.completed ? 'Completed — click to clear'
              : message.flagged ? 'Flagged — click to mark complete'
              : 'Flag'
            }
            onClick={() => onFlagCycle && onFlagCycle(message.id)}
          >
            <Icon name={message.completed ? 'check' : 'flagOutline'} size={14} stroke={message.completed ? 2.4 : 1.6} />
          </button>
          <button
            className={`${styles.tbBtn} ${styles.tbBtnIcon}`}
            title="Mark as unread"
            onClick={() => onMarkUnread(message.id)}
          >
            <Icon name="envelopeClosed" size={14} />
          </button>
          <button
            className={`${styles.tbBtn} ${styles.tbBtnIcon}`}
            title="Delete"
            onClick={() => onDelete(message.id)}
          >
            <Icon name="trashThin" size={14} />
          </button>
          <button className={`${styles.tbBtn} ${styles.tbBtnIcon}`} title="Print">
            <Icon name="print" size={14} />
          </button>

          {/* 3-dot "More" menu — currently hosts the Download-email action. */}
          <span className={styles.moreWrap}>
            <button
              className={`${styles.tbBtn} ${styles.tbBtnIcon}`}
              title="More"
              onClick={(e) => { e.stopPropagation(); setMoreOpen(o => !o); }}
            >
              <Icon name="dots" size={14} />
            </button>
            {moreOpen && (
              <div className={styles.moreMenu} onClick={(e) => e.stopPropagation()}>
                {onManageReplyOpeners && (
                  <button
                    onClick={() => {
                      // Whatever is selected in the email seeds the phrase — the
                      // body lives in an iframe, so ask it first.
                      let seed = '';
                      try { seed = String(iframeRef.current?.contentWindow?.getSelection?.() || ''); } catch { /* sandboxed */ }
                      if (!seed) { try { seed = String(window.getSelection?.() || ''); } catch { /* none */ } }
                      setMoreOpen(false);
                      onManageReplyOpeners(seed.replace(/\s+/g, ' ').trim().slice(0, 120));
                    }}
                  >
                    Auto responses…
                  </button>
                )}
                <button
                  disabled={downloading}
                  onClick={async () => {
                    setDownloading(true);
                    try {
                      // Fetch-and-blob, NOT a raw <a href> navigation. Two reasons:
                      // (1) the global window.fetch wrapper (src/index.js) stamps
                      //     X-Impersonate-Mailbox on /api/* calls, so the .eml comes
                      //     from the OPEN mailbox. A bare <a href> navigation can't
                      //     carry that header, so when an admin has another mailbox
                      //     open the request hits Graph against the admin's OWN
                      //     mailbox, 404s on the message id, and the browser shows
                      //     "Site wasn't available". Attachments already work here
                      //     because saveAs() goes through fetch+blob.
                      // (2) blob: bytes survive Chrome Safe Browsing, which kills
                      //     same-origin <a href download> with the same error.
                      const res = await fetch(
                        `/api/mail/messages/${encodeURIComponent(message.id)}/download`,
                        { credentials: 'same-origin' }
                      );
                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                      const blob = await res.blob();
                      const url  = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = (message.subject ? message.subject.replace(/[\\/:*?"<>|]/g, '_').slice(0, 80) : 'email') + '.eml';
                      document.body.appendChild(a); a.click(); a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    } catch (err) {
                      // eslint-disable-next-line no-alert
                      alert('Download failed: ' + (err.message || 'unknown error'));
                    } finally {
                      setDownloading(false);
                      setMoreOpen(false);
                    }
                  }}
                >
                  {downloading ? 'Preparing…' : 'Download email (.eml)'}
                </button>
              </div>
            )}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className={styles.body}>
        <div className={styles.inner}>
          <div className={styles.sender}>
            <div className={styles.oneline}>
              <b className={styles.onelineName}>{message.fromName || message.from}</b>
              {message.fromName && (
                // Email-address line is desktop-only — on mobile the
                // sender's name alone is enough, and dropping it lets the
                // date fit on the same row instead of wrapping below.
                <span className={`${styles.onelineEmail} ${styles.desktopOnly}`}>&lt;{message.from}&gt;</span>
              )}

              {/* Subject takes the slot that used to host the green
                  "Existing Contact" reassurance pill. The full email
                  subject now lives one click away from the sender row,
                  matching how Outlook / Gmail surface it. The "New
                  Contact" / "New Customer" prompt has moved into the
                  topbar (alongside Add Sample) so it reads as a
                  context-action on the email rather than a sender
                  disclaimer. */}
              {message.subject && (() => {
                // Mirror MessageList.js' stripUrgentMarker so the word
                // doesn't show alongside the URGENT pill / importance
                // banner. Earlier fix only covered the message-list
                // row; the reading-pane subject header was missed,
                // which is why the regression resurfaced.
                const stripped = String(message.subject)
                  .replace(/[\[\(\*]+\s*urgent\s*[\]\)\*]+[\s!:.\-]*/gi, ' ')
                  .replace(/\burgent\b[\s!:.\-]*/gi, ' ')
                  .replace(/\s{2,}/g, ' ')
                  .replace(/^\s*[:\-]+\s*/, '')
                  .trim();
                const display = stripped || message.subject;
                return (
                  <span className={styles.onelineSubject} title={message.subject}>
                    · {display}
                  </span>
                );
              })()}

              <span className={styles.onelineSpacer} />
              <span className={styles.onelineDate}>{formatFullDate(message.date)}</span>
            </div>

            {message.toAddresses?.length > 0 && (() => {
              // Hide the "To: me" line when there's a single recipient with no
              // real name (or the placeholder "me"). Matches the latest prototype.
              const showable = message.toAddresses.filter(
                a => a.name && !/^me$/i.test(a.name)
              );
              if (!showable.length) return null;
              return (
                <div className={styles.senderTo}>
                  To:{' '}
                  {showable.map((a, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      <strong>{a.name}</strong> &lt;{a.email}&gt;
                    </span>
                  ))}
                </div>
              );
            })()}
            {message.ccAddresses?.length > 0 && (
              <div className={styles.senderTo}>
                Cc:{' '}
                {message.ccAddresses.map((a, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    {a.name ? <><strong>{a.name}</strong> &lt;{a.email}&gt;</> : a.email}
                  </span>
                ))}
              </div>
            )}

            {(() => {
              const list = message.attachments || [];
              if (!list.length) return null;
              // Filter out noise attachments (Defender ATP scan
              // fragments, postmaster headers, log/trace .txt files).
              // Single source of truth lives in MessageList.js so the
              // row paperclip and the reading-pane chip list always
              // agree about what's noise.
              const visible = list.filter(a => !isNoiseAttachment(a));
              if (!visible.length) return null;
              return (
                <div className={styles.attachments}>
                  {visible.map(a => {
                    // withImpersonate: these are plain <a href> navigations —
                    // the fetch interceptor can't stamp the impersonation
                    // header on them, so the target rides as ?as= instead.
                    const base = `/api/mail/messages/${encodeURIComponent(message.id)}/attachments/${encodeURIComponent(a.id)}`;
                    return (
                      <AttachmentChip
                        key={a.id}
                        att={a}
                        downloadUrl={withImpersonate(base)}
                        openUrl={withImpersonate(`${base}?inline=1`)}
                        previewUrl={withImpersonate(`${base}?preview=1`)}
                        onPreview={(att, url) => setPreview({ att, url })}
                        designPointUrl={designPointEnabled ? `${base}/designpoint` : null}
                      />
                    );
                  })}
                </div>
              );
            })()}
            {/* No "Loading attachments…" placeholder — Graph's
                hasAttachments is true for inline-only signatures and
                the noise files we strip, so the chip used to flash on
                every email and then vanish. Real attachments still
                appear silently once the fetch returns. */}
          </div>

          {aiError && <div className={styles.aiError}>⚠ {aiError}</div>}

          {message?.attachmentsUnavailable && (
            <div className={styles.aiError} style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
              ⚠ Attachments are taking longer than usual to load — retrying automatically in the background. If they don't appear shortly, click the email again.
            </div>
          )}

          <div className={styles.emailBody}>
            {bodyHtml ? (
              <iframe
                ref={iframeRef}
                className={styles.iframe}
                scrolling="auto"
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes"><base target="_blank"><style>
                  /* Minimal reset — keep out of the way so the email's own
                     inline styles (signatures, marketing templates) render as
                     the sender intended. Same shape used in ComposeModal so
                     view and reply look identical. */
                  html, body { margin: 0; padding: 0; word-wrap: break-word; }
                  /* Force the iframe document to size to its content
                     rather than fill its parent. Some forwarded chains
                     ship html/body with height:100vh / overflow:auto
                     baked in (Outlook desktop in particular) — those
                     would otherwise create an inner scrollbar inside
                     our auto-sizing iframe. !important defeats both
                     <style> rules inside the email body and any
                     residue from the inline-style scrub. */
                  html, body { height: auto !important; min-height: 0 !important; max-height: none !important; overflow: visible !important; }
                  /* Same rule applied to every layout container in the
                     email body. Some forwarded chains (Outlook desktop
                     in particular) put height:100vh / overflow:auto on
                     a wrapper class — defined inside an inline <style>
                     block, not as inline style — so the per-element
                     scrub couldn't see it. Targeting tag-level
                     selectors here defeats those rules without
                     touching images / videos / explicit-size embeds. */
                  div, table, tbody, thead, tr, td, th, section, article, main, aside, header, footer, nav {
                    height: auto !important;
                    min-height: 0 !important;
                    max-height: none !important;
                    overflow: visible !important;
                  }
                  /* No zoom or width tricks here — the dashboard <body>
                     applies a 0.9 page-level zoom (App.module.css) so
                     everything (chrome + iframe + email content) is
                     scaled together. The iframe body just renders at
                     native size to fill its parent. */
                  body { font-family: Calibri, -apple-system, 'Segoe UI', Arial, sans-serif; font-size: 11pt; line-height: 1.35; color: #1b1c1f; }${isV2() ? `
                  /* /V2: the app's text is ~2px smaller (v2/theme.css), and
                     the email follows. Most mail sets its own inline sizes,
                     so scale the whole document rather than the default
                     font: 11pt (14.67px) → ~12.67px. The iframe has a fixed
                     height and scrolls itself, so nothing measures this. */
                  html { zoom: 0.864; }
                  /* /V2 desktop: this frame runs to the pane's right edge
                     and is the reading pane's only scroller (the header
                     above it is pinned — ReadingPane.module.css), so keep
                     the text off the scrollbar and give the scrollbar the
                     app's slim style. */
                  @media (min-width: 769px) {
                    body { padding-right: 32px; padding-bottom: 20px; }
                  }
                  ::-webkit-scrollbar { width: 10px; height: 10px; }
                  ::-webkit-scrollbar-thumb { background: #cdd1e1; border-radius: 10px; border: 2px solid #fff; }
                  ::-webkit-scrollbar-thumb:hover { background: #a3a7bb; }
                  ::-webkit-scrollbar-button { display: none; }` : ''}
                  /* Cap the body to a typical email width (~1000px). Most
                     marketing templates and signatures are designed for
                     600-800px; if we let a 1200px reading pane stretch
                     them to full width, multi-column signature tables
                     (logo | contact | awards) re-proportion against the
                     wider area and the layout looks broken. 1000px is
                     wide enough that paragraphs aren't cramped, narrow
                     enough that signatures keep their intended cell
                     widths. */
                  body { max-width: 1000px; padding: 0 4px; box-sizing: border-box; }
                  /* Defeat the centred-narrow-column wrapper that Outlook
                     and many marketing senders inject as the first child
                     of body (a div with max-width 600px and margin 0
                     auto, or a table aligned centre at width 600). We
                     strip max-width and the auto-margins so the content
                     left-aligns inside the 1000px body cap. We deliberately
                     do NOT force width:100% on the wrapper — that was
                     the old behaviour and it broke signatures: nested
                     multi-cell tables proportion against the wrapper, so
                     stretching the wrapper to 100% spread the signature
                     cells too wide for their intended content. Letting
                     the wrapper size to its natural content width keeps
                     signatures rendering as the sender designed them. */
                  body > div[style*="max-width"],
                  body > div[style*="margin: 0 auto"],
                  body > div[style*="margin:0 auto"],
                  body > table[align="center"],
                  body > center > table,
                  body > center {
                    max-width: none !important;
                    margin-left: 0 !important;
                    margin-right: 0 !important;
                  }
                  /* Only resize images that have NO width set. Signature
                     images with an explicit width attribute keep their
                     intended size (the old rule squashed them horizontally
                     inside fixed-width signature tables). */
                  img:not([width]):not([style*="width"]) { max-width: 100%; height: auto; }
                  /* Outlook authors signatures as <p class="MsoNormal">
                     with margin:0 declared in an embedded <style> block
                     at the top of the email. That stylesheet often
                     doesn't survive transit through Graph / our HTML
                     processing — the class name remains on the <p> but
                     the rule that gives it margin:0 is gone. Default
                     browser <p> margin is ~16px top/bottom, so each
                     line of the signature gets pushed apart and looks
                     like double-spacing. Restore the Outlook default
                     directly via the class name + Mso* twin classes,
                     plus a catch-all for paragraphs inside any table
                     cell (signatures live in <table><tr><td><p>...).
                     Email-body paragraphs aren't normally inside td/th
                     so their natural spacing is preserved. */
                  p.MsoNormal, p.MsoNoSpacing, p.MsoListParagraph,
                  td p, th p { margin: 0; }
                  /* Desktop: allow wide signature tables to scroll inside
                     the body so the parent layout isn't pushed sideways.
                     Vertical scroll is owned by the parent reading pane —
                     the iframe sizes to its content height (see onLoad
                     handler below) so it never needs its own vertical
                     scrollbar. Forcing overflow-y:visible here means even
                     if some inline style on the email tries to set
                     overflow-y:auto on body / html, no second scrollbar
                     appears. */
                  html, body { overflow-y: visible !important; }
                  body { overflow-x: auto; }
                  /* DESKTOP: do NOT cap wide images here. The earlier
                     rule "img:not([width])" handles imgs without an
                     explicit width attribute; signature images that
                     ship with hard-coded width=160 / width=600 are
                     sized that way for a reason, and capping them
                     reflows the surrounding signature table into a
                     narrower column. The mobile media query below
                     keeps a hard cap because phones really do need
                     it; desktop reading panes are wide enough to
                     show natural sizes, and the body overflow-x:auto
                     handles a rare wider-than-pane image without
                     breaking layout. */
                  blockquote:not([style]) { border-left: 3px solid #e3dfd7; margin: 12px 0; padding-left: 12px; color: #4b4e55; }

                  /* ── Mobile (≤ 768 px) ─────────────────────────────────
                     The reading pane IS the whole screen on mobile — a
                     left/right scrollbar inside it lets the user "move
                     off" the visible email and lose orientation. Lock
                     overflow, force every common wide element to a sane
                     width, and downscale the body so wide marketing
                     templates fit instead of clipping. */
                  @media (max-width: 768px) {
                    html, body { overflow-x: hidden !important; max-width: 100vw; }
                    body { zoom: 0.78; }
                    img, video, iframe, object, embed { max-width: 100% !important; height: auto; }
                    /* Tables with fixed pixel widths get clamped — better
                       to wrap a hard-coded 800 px table than show 200 px
                       of empty whitespace and a horizontal scrollbar. */
                    table { max-width: 100% !important; }
                    /* Any element with an inline width: NNNpx wider than
                       the viewport gets capped. The CSS attribute selector
                       can't math, so we just nuke fixed widths > viewport
                       via a pragmatic catch-all. */
                    [width], [style*="width:"] { max-width: 100% !important; }
                    pre, code { white-space: pre-wrap; word-break: break-word; }
                  }
                </style></head><body>${bodyHtml}<style>
                  /* Cascade-last reset for layout containers only. Any
                     <style> the email body contains was rendered above
                     this block, so these !important rules win the tie
                     when an email <style> uses a class-scoped rule
                     (which the head-block CSS can't beat). Carved out
                     to layout tags so we don't collapse <img>/<video>
                     heights the email is depending on. */
                  html, body, div, table, tbody, thead, tfoot, tr, td, th,
                  section, article, main, aside, header, footer, nav, center,
                  p, span, blockquote, ul, ol, li, fieldset, form {
                    height: auto !important; min-height: 0 !important;
                    max-height: none !important; overflow: visible !important;
                    overflow-y: visible !important;
                  }
                </style></body></html>`}
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
                title="Email body"
                onLoad={() => {
                  // Autosize is handled by the useEffect above which
                  // runs deterministically after every bodyHtml
                  // change (this onLoad was unreliable when React
                  // mutated srcDoc on the existing iframe). All this
                  // handler does now is install the click-relay so
                  // outside-click handlers (right-click menu close,
                  // dropdown dismiss) fire when the user clicks
                  // inside the iframe.
                  try {
                    const iframe = iframeRef.current;
                    const doc = iframe?.contentDocument;
                    if (doc && !doc.__ppClickRelay) {
                      doc.__ppClickRelay = true;
                      doc.addEventListener('mousedown', () => {
                        window.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                      });
                    }
                  } catch {}
                }}
              />
            ) : fullBodyLoaded ? (
              <div className={styles.noBody}>No message body</div>
            ) : message?._bodyLoadError ? (
              // The body fetch failed or timed out. Show the reason instead
              // of spinning forever — full timing detail is in the top-bar
              // Debug panel. Re-clicking the email retries.
              <div className={styles.bodyLoading} style={{ color: '#b91c1c' }}>
                <span>⚠ Couldn't load this message: {message._bodyLoadError}. Click the email again to retry, or open <strong>Debug</strong> (top bar) for details.</span>
              </div>
            ) : (
              // Body is still being fetched from Graph. Previously this
              // rendered an empty div, so a slow getMessage looked like a
              // broken white screen. Show an explicit loading state.
              <div className={styles.bodyLoading} aria-busy="true">
                <span className={styles.aiSpinner} />
                <span>Loading message…</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick-reply bar removed per UX request — the draft-tabs strip
          (rendered below) is now the persistent reply/compose surface.
          Users open a full reply via the top-toolbar Reply / Reply All
          / Forward / AI Reply buttons, which open the compose modal in
          the same composeSlot — the draft tab for that compose then
          appears in the strip and survives navigation. */}
      </>)}

      {/* Use the shared renderDraftTabs() helper so this strip stays
          in lock-step with the empty-state branch above. Any change to
          tab rendering only needs to happen in one place. */}
      {renderDraftTabs()}

      {addingContact && (
        <AddContactModal
          message={message}
          senderInfo={senderInfo}
          onClose={onCloseAddContact}
          onAdded={onContactAdded}
        />
      )}
      {updatingContact && (
        <UpdateContactModal
          message={message}
          senderInfo={senderInfo}
          onClose={onCloseUpdateContact}
          onUpdated={onContactUpdated}
          onSwitchToAdd={onSwitchToAddContact}
        />
      )}
      {/* composeOverlay is rendered up the tree as a flex slot now —
          no trailing render here so it doesn't double-mount. */}
      {preview && (
        <AttachmentPreviewOverlay
          att={preview.att}
          url={preview.url}
          onClose={() => setPreview(null)}
        />
      )}
    </main>
  );
}

// Exported for its render test (ReadingPane.attachmentMenu.test.js).
export { AttachmentChip };
